<template>
  <div id="main-content">
    <ApiLoading class="loading-wrapper" v-if="loading.members || loading.contents || loading.coverDetails" :apiError="false" isMemberPortal />

    <template v-else>
      <section class="content-page-banner">
        <div class="banner-content">
          <h1 class="heading">{{ $route.meta.title }}</h1>
          <div class="underline"></div>
          <div v-if="!loading.contents && !error.contents" v-html="contents.info" />
        </div>
      </section>
      <Alert v-if="!loading.members && !loading.contents && (error.contents || error.members || error.coverDetails)" alertType="error" />
      <section class="fr-view">
        <div v-html="contents.callOut" />
      </section>
      <section>
        <h2>Members</h2>
        <div class="grey-line"></div>
        <ApiLoading v-if="error.members || error.coverDetails" :apiError="error.members || error.coverDetails" isMemberPortal />

        <ol v-else class="member-wrapper">
          <li class="member" v-for="member in members" :key="`${member.firstName}-${member.lastName}`">
            <MemberTag :firstName="member.firstName" :lastName="member.lastName" :relationship="member.coveredOnMembership" />

            <router-link
              v-if="member.personCode === currentMemberDetail.personCode || (!member.keepPrivateflag && canAccess)"
              class="button secondary"
              :to="{
                ...toMemberDetails,
                params: { personCode: member.personCode, name: `${member.firstName} ${member.lastName}` },
                query: { step: 1 },
              }"
            >
              View and edit
            </router-link>
            <button v-else disabled>View and edit</button>
          </li>
        </ol>
      </section>

      <section v-if="!loading.contents && !error.contents && contents.relatedArticles" id="related-pages">
        <h2>Related articles</h2>
        <QuickLinks :linksData="contents.relatedArticles" />
      </section>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import MemberDetailsServices from '@/services/MemberDetails';
import { Alert, ApiLoading, MemberTag, QuickLinks } from '@/components';
import { MY_DETAILS_VIEW_UPDATE_DETAILS } from '@/router/routeNames';
import { AccessLevel } from '@/enums';
import { VUEX_GENERAL, VUEX_MEMBER_DETAILS } from '@/constants/VuexConstants';
import { getAccessLevel, sortMembersByRelationshipDisplay } from '@/utils';

export default {
  name: 'MemberSelection',

  components: {
    Alert,
    ApiLoading,
    MemberTag,
    QuickLinks,
  },

  computed: {
    ...mapGetters({
      currentMemberDetail: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
      memberships: VUEX_GENERAL.GETTERS.memberships,
    }),

    toMemberDetails() {
      return { name: MY_DETAILS_VIEW_UPDATE_DETAILS };
    },

    canAccess() {
      const thisAccessLevel = getAccessLevel(this.$route.params.policyNumber, this.memberships);
      return thisAccessLevel === this.AccessLevel.FULL_ACCESS || thisAccessLevel === this.AccessLevel.POLICY_HOLDER_ACCESS;
    },
  },

  data() {
    return {
      AccessLevel,
      members: [],
      contents: {},
      coverDetails: {},
      loading: {
        contents: true,
        members: true,
        coverDetails: true,
      },
      error: {
        contents: false,
        members: false,
        coverDetails: false,
      },
    };
  },

  mounted() {
    this.resetMemberDetails();
    this.getPageData();
  },

  methods: {
    ...mapMutations('MemberDetails', {
      resetMemberDetails: VUEX_MEMBER_DETAILS.MUTATIONS.resetMemberDetails,
      updateCoverDetails: VUEX_MEMBER_DETAILS.MUTATIONS.updateCoverDetails,
    }),

    // take array member object and sort by first name alphabetically
    sortMemberListByFirstName(arr) {
      return arr.sort((a, b) => {
        if (a.firstName < b.firstName) return -1;
        if (a.firstName > b.firstName) return 1;
        return 0;
      });
    },

    async getPageData() {
      // load page contents
      try {
        const data = await MemberDetailsServices.getMemberSelectionContents();
        this.contents = {
          info: data?.memberSelectionInformationText?.informationText,
          callOut: data?.memberSelectionCalloutText?.informationText,
          relatedArticles: [data?.relatedArticle1, data?.relatedArticle2, data?.relatedArticle3],
        };
      } catch (error) {
        logger.error(error);
        this.error.contents = true;
      } finally {
        this.loading.contents = false;
      }

      // load members
      try {
        const members = await MemberDetailsServices.getMembers(this.$route.params.policyNumber);
        this.members = sortMembersByRelationshipDisplay(members);
      } catch (error) {
        logger.error(error);
        this.error.members = true;
      } finally {
        this.loading.members = false;
      }

      // Load cover details
      try {
        const coverDetails = await MemberDetailsServices.getCoverDetails(this.$route.params.policyNumber);
        this.coverDetails = coverDetails;
        this.updateCoverDetails(coverDetails);
      } catch (error) {
        logger.error(error);
        this.error.coverDetails = true;
      } finally {
        this.loading.coverDetails = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.top-content {
  background-color: $PRIMARY_GREEN_TINT;
  width: 100%;
  max-width: 1216px;
  margin: 0 auto;

  &.extended-full-width::before {
    content: '';
    height: 100%;
    width: 100vw;
    position: absolute;
    top: 0;
    right: 50%;
    transform: translateX(50%);
    z-index: -1;
    background-color: $PRIMARY_GREEN_TINT;
  }

  .extended-full-width {
    background: $GREY_50;
    margin-top: rem(-50);
  }

  .top-section-content-container {
    display: flex;
    flex-direction: column;
    padding: 3rem 0;
    max-width: 680px;
  }
}

section:not(.top-content) {
  margin-bottom: 100px;

  &:last-child {
    margin-bottom: 0;
  }

  & > h2 {
    font-size: font-size(2);
    margin-top: 0;
  }

  .member-wrapper {
    @include grid-layout(8px);
    @include grid-template(repeat(auto-fill, minmax(275px, 1fr)));
    row-gap: 28px;

    .member {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    a.button.secondary,
    button {
      transform: translateX(92px);
    }
  }

  &.fr-view {
    margin: 3.5rem 0;
  }
}

.heading {
  margin-bottom: 10px;
}

.grey-line {
  background-color: $GREY_100;
  width: 100%;
  height: 2px;
  margin-bottom: 20px;
}

#related-pages {
  margin-bottom: 3rem;
}
</style>
